import { IComboBoxOption } from '@fluentui/react';
import LazyInitialization from '../../lib/LazyInitialization';
import XhrRequestHandler from '../../lib/XhrRequestHandler';
import { Collaborator } from '../collaborators/Collaborator';
import { User } from '../User';
import { OrganizationChartIntermediateNode } from './OrganizationChartIntermediateNode';
import { OrganizationChartLevel } from './OrganizationChartLevel';
import { OrganizationChartNode } from './OrganizationChartNode';
import { OrganizationChartTopNode } from './OrganizationChartTopNode';
import { NodoOrganigramaDataGrid } from './nodo-organigrama-datagrid.interface';

interface IOrganizationChartLevel {
  nio_id: string;
  nio_nombre: string;
  nio_nombre_tec: string;
}

interface IIComboBoxOptionLeves {
  levels:IComboBoxOption[]
}

export interface IOrganizationChartNode {
  nor_id: string;
  nor_nombre: string;
  nor_nivel: string;
  nor_padre: string | null;
}

export interface IOrganizationChartCompleteNode extends IOrganizationChartNode {
  nodoOrganigrama: IOrganizationChartCompleteNode | null;
}

export interface SaveCostCentersResponse {
  success: boolean;
  updated: number;
  errors: string[];
}

const TAXTECH_ROOT_NODE_NAME = 'TaxTech';
const GRZ_ROOT_NODE_NAME = 'GRZ';

const EXCLUDED_ROOT_NODE_NAMES = [TAXTECH_ROOT_NODE_NAME, GRZ_ROOT_NODE_NAME];

export const normalizeNodeId = (value: string | number) => String(value).replace(/^0+/, '') || '0';

export const getHiddenRootNodeIdsFromRows = (rows: { nor_id: string | number; nor_nombre: string; nor_padre: string | null }[]): string[] => {
  const roots = rows.filter((row) => EXCLUDED_ROOT_NODE_NAMES.includes(row.nor_nombre) && !row.nor_padre);
  if (roots.length === 0) {
    return [];
  }
  const subtreeIds = new Set<string>(roots.map((root) => normalizeNodeId(root.nor_id)));
  let grew = true;
  while (grew) {
    grew = false;
    for (const row of rows) {
      if (row.nor_padre && subtreeIds.has(normalizeNodeId(row.nor_padre)) && !subtreeIds.has(normalizeNodeId(row.nor_id))) {
        subtreeIds.add(normalizeNodeId(row.nor_id));
        grew = true;
      }
    }
  }
  return [...subtreeIds];
};


class OrganizationChartSystem {
  private readonly entityBase = '/organigrama';

  private levels: LazyInitialization<OrganizationChartLevel[]>;
  private nodes: LazyInitialization<OrganizationChartNode[]>;

  constructor(private requestHandler: XhrRequestHandler) {
    this.levels = new LazyInitialization(() => this.getLevelsRequest());
    this.nodes = new LazyInitialization(() => this.getAllNodesRequest());
  }

  getBaseUrlForDatagrid() {
    return this.requestHandler.requestUrl(`${this.entityBase}/datagrid`);
  }

  async getDataGrid(limit: number, filters?: { name: string; value: string; operator: string }[]) {
    const params = new URLSearchParams();
    params.append('skip', '0');
    params.append('limit', limit.toString());

    if (filters && filters.length > 0) {
      filters.forEach((filter, index) => {
        params.append(`filter[${index}][name]`, filter.name);
        params.append(`filter[${index}][value]`, filter.value);
        params.append(`filter[${index}][operator]`, filter.operator);
      });
    }

    const url = `${this.entityBase}/datagrid?${params.toString()}`;
    const result = await this.requestHandler.get<NodoOrganigramaDataGrid>(url);

    const excludedIds = new Set(this.getHiddenRootNodeIdsFromRows(result.data));
    result.data = result.data.filter((row) => !excludedIds.has(normalizeNodeId(row.nor_id)));

    return result;
  }


  async saveCostCenters(data: { nor_id: string; nor_cecos: string | null }[]) {
    return this.requestHandler.post<SaveCostCentersResponse>(`${this.entityBase}/save-cost-centers`, data );
  }

  async createOrganigramaAndCostCenters(data: { isCore: boolean; costCenterName: string; 'Estructura Organizacional': any[] }) {
    return this.requestHandler.post<{
        "success": boolean,
        "nor_id": number,
        "nor_cecos": string,
        "nor_es_core": number
    }>(`${this.entityBase}/create-organigrama`, data);
  }


  getLevels(reload = false): Promise<OrganizationChartLevel[]> {
    return this.levels.value(reload);
  }

  async getLastLevel(): Promise<OrganizationChartLevel> {
    const l = await this.getLevels();
    return [...l].pop()!;
  }

  async getAllNodes(reload = false): Promise<OrganizationChartNode[]> {
    const nodes = await this.nodes.value(reload);
    return this.excludeHiddenRootNodes(nodes);
  }

  async getLastLevelNodes(): Promise<OrganizationChartNode[]> {
    const [nodes, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartCompleteNode[]>(`${this.entityBase}/last-level-nodes`),
      this.getLevels(),
    ]);

    return nodes.map((node) => this.mapNode(node, levels));
  }

  async getLastLevelNodesss(level:string): Promise<IIComboBoxOptionLeves> {
    const [results, nodes] = await Promise.all([
      this.requestHandler.get<IIComboBoxOptionLeves>(`${this.entityBase}/level-all-nodes/${level}`),
      this.nodes.value(),
    ]);

    // Excluir los subárboles de TaxTech y GRZ de las opciones
    const excludedIds = new Set(this.getHiddenRootNodeIds(nodes).map(normalizeNodeId));

    return {
      ...results,
      levels: (results.levels ?? []).filter((option) => !excludedIds.has(normalizeNodeId(option.key))),
    };
  }

  async getNode(nodeId: string): Promise<OrganizationChartNode> {
    return this.nodes.withCachedValueDo(
        (data) => {
          const node = data.find((node) => node.isIdentifiedBy(nodeId));
          if (!node) {
            throw new Error('Node not found');
          }
          return Promise.resolve(node);
        },
        () => this.getNodeByIdRequest(nodeId)
    );
  }

  private async getNodeByIdRequest(nodeId: string): Promise<OrganizationChartNode> {
    const [node, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartCompleteNode>(`${this.entityBase}/node/${nodeId}`),
      this.getLevels(),
    ]);

    return this.mapNode(node, levels);
  }

  mapNode(node: IOrganizationChartCompleteNode, levels: OrganizationChartLevel[]): OrganizationChartNode {
    const level = this.levelById(levels, Number(node.nor_nivel));
    if (node.nodoOrganigrama) {
      const parent = this.mapNode(node.nodoOrganigrama, levels);
      return OrganizationChartIntermediateNode.named(node.nor_nombre, node.nor_id, level, parent);
    }
    return OrganizationChartTopNode.named(node.nor_nombre, node.nor_id, level);
  }

  private levelById(levels: OrganizationChartLevel[], l: number): OrganizationChartLevel {
    return levels.find((level) => level.isIdentidiedBy(l))!;
  }

  private async getLevelsRequest(): Promise<OrganizationChartLevel[]> {
    const data = await this.requestHandler.get<IOrganizationChartLevel[]>(`${this.entityBase}/levels`);

    return data
        .sort((a, b) => Number(a.nio_id) - Number(b.nio_id))
        .map((level) => OrganizationChartLevel.named(level.nio_nombre, Number(level.nio_id), level.nio_nombre_tec));
  }

  private async getAllNodesRequest(): Promise<OrganizationChartNode[]> {
    const [nodes, levels] = await Promise.all([
      this.requestHandler.get<IOrganizationChartNode[]>(`${this.entityBase}/nodes`),
      this.getLevels(),
    ]);

    const result: OrganizationChartNode[] = [];
    nodes
        .sort((a, b) => Number(a.nor_nivel) - Number(b.nor_nivel))
        .forEach((node) => {
          const level = this.levelById(levels, Number(node.nor_nivel));
          if (node.nor_padre) {
            const parent = result.find((possibleParent) =>
                possibleParent.isIdentifiedBy(node.nor_padre || '')
            )!;
            result.push(OrganizationChartIntermediateNode.named(node.nor_nombre, node.nor_id, level, parent));
          } else {
            result.push(OrganizationChartTopNode.named(node.nor_nombre, node.nor_id, level));
          }
        });

    return result;
  }

  public async getNodeOf(col: Collaborator) {
    let pos = await col.getPosition();
    let node = await pos.getOrganizationNode();
    return node;
  }

  async getAvailableNodesOf(user: User) {
    const filtersDataNodes = await this.getAllNodes();
    const nodeOfLoggedCol = await this.getNodeOf(user.getCollaborator());
    let _nodesFiltered = this.excludeHiddenRootNodes(filtersDataNodes);
    if (!user.hasPowerUserPrivileges()) {
      _nodesFiltered = _nodesFiltered.filter((node) => node.isDescendantOf(nodeOfLoggedCol));
    }
    return _nodesFiltered;
  }

  excludeHiddenRootNodes(nodes: OrganizationChartNode[]): OrganizationChartNode[] {
    return EXCLUDED_ROOT_NODE_NAMES.reduce(
      (acc, rootName) => this.excludeNodesOfRoot(acc, rootName),
      nodes
    );
  }

  getHiddenRootNodeIds(nodes: OrganizationChartNode[]): string[] {
    return EXCLUDED_ROOT_NODE_NAMES.reduce<string[]>((ids, rootName) => {
      const root = this.findRootNode(nodes, rootName);
      if (!root) {
        return ids;
      }
      return ids.concat(nodes.filter((node) => node.isDescendantOf(root)).map((node) => node.getId()));
    }, []);
  }

  getHiddenRootNodeIdsFromRows(rows: IOrganizationChartNode[]): string[] {
    return getHiddenRootNodeIdsFromRows(rows);
  }

  private excludeNodesOfRoot(nodes: OrganizationChartNode[], rootName: string): OrganizationChartNode[] {
    const root = this.findRootNode(nodes, rootName);
    if (!root) {
      return nodes;
    }
    return nodes.filter((node) => !node.isDescendantOf(root));
  }

  private findRootNode(nodes: OrganizationChartNode[], rootName: string): OrganizationChartNode | undefined {
    return nodes.find((node) => node.getName() === rootName && node.parentPath() === '');
  }

}

export default OrganizationChartSystem;
